<template>
    <section class="question-builder">
  
      <div class="builder-header">
  
        <div>
          <h4>
            {{ title }}
          </h4>
  
          <p>
            {{ description }}
          </p>
        </div>
  
      </div>
  
      <div
        v-if="questions.length === 0"
        class="empty-state"
      >
        No questions added.
      </div>
  
      <div
        v-for="(question, index) in questions"
        :key="question.id ?? index"
        class="question-row"
      >
  
        <div class="question-heading">
  
          <span>
            Question {{ index + 1 }}
          </span>
  
          <button
            type="button"
            class="remove-button"
            @click="$emit('remove', index)"
          >
            Remove
          </button>
  
        </div>
  
        <label>
          Question
  
          <input
            v-model="question.question"
            type="text"
            placeholder="Enter the question"
          />
        </label>
  
        <div class="question-options">
  
          <label>
            Response Type
  
            <select
              :value="question.respType"
              @change="updateResponseType(question, $event.target.value)"
            >
              <option value="Keyboard">
                Keyboard
              </option>
  
              <option value="Number">
                Number
              </option>
  
              <option value="YesNo">
                Yes / No
              </option>
  
            </select>

            <div class="reorder-control">
              <span class="reorder-label">
                Reorder
              </span>

              <span class="reorder-divider" aria-hidden="true">
                |
              </span>

              <button
                type="button"
                class="reorder-button"
                :disabled="index === 0"
                :aria-label="`Move question ${index + 1} up`"
                title="Move question up"
                @click="$emit('move', { index, direction: -1 })"
              >
                <span class="material-icons-outlined" aria-hidden="true">
                  arrow_upward
                </span>
              </button>

              <button
                type="button"
                class="reorder-button"
                :disabled="index === questions.length - 1"
                :aria-label="`Move question ${index + 1} down`"
                title="Move question down"
                @click="$emit('move', { index, direction: 1 })"
              >
                <span class="material-icons-outlined" aria-hidden="true">
                  arrow_downward
                </span>
              </button>
            </div>
          </label>
  
          <label>
            Quick Answers*
  
            <select
              :value="selectedQuickAnswer(question)"
              class="quick-answer-select"
              :class="{
                'quick-answer-placeholder':
                  !selectedQuickAnswer(question)
              }"
              :disabled="question.respType === 'YesNo'"
              @change="updateQuickAnswer(question, $event.target.value)"
            >
              <option
                value=""
                class="quick-answer-placeholder-option"
              >
                See Quick Answers
              </option>

              <option
                v-for="option in optionalQuickAnswers"
                :key="option"
                :value="option"
                class="quick-answer-option"
              >
                {{ option }}
              </option>
            </select>
          </label>
  
        </div>
  
      </div>

      <button
        type="button"
        class="add-question-button"
        @click="$emit('add')"
      >
        Add Question
      </button>
  
    </section>
  </template>
  
  <script>
  export default {
    name:
      "LitigationQuestionBuilder",
  
    props: {
      title: {
        type:
          String,
        required:
          true
      },
  
      description: {
        type:
          String,
        default:
          ""
      },
  
      questions: {
        type:
          Array,
        required:
          true
      }
    },
  
    emits: [
      "add",
      "remove",
      "move"
    ],
  
    setup() {

      const optionalQuickAnswers = [
        "Skip",
        "Next",
        "N/A"
      ];

      const updateResponseType =
        (
          question,
          responseType
        ) => {

          question.respType =
            responseType;

          if (
            responseType !==
            "Keyboard" &&
            responseType !==
            "Number"
          ) {

            question.respOpts =
              [];
          }
        };
  
      const selectedQuickAnswer =
        question => {

          const selected =
            question.respOpts?.[0];

          switch (
            String(selected ?? "")
              .trim()
              .toLowerCase()
          ) {
          case "skip":
            return "Skip";

          case "next":
            return "Next";

          case "n/a":
          case "na":
          case "not applicable":
          case "notapplicable":
            return "N/A";

          default:
            return "";
          }
        };

      const updateQuickAnswer =
        (
          question,
          value
        ) => {
  
          question.respOpts =
            value ?
              [value] :
              [];
        };
  
      return {
        optionalQuickAnswers,
        updateResponseType,
        selectedQuickAnswer,
        updateQuickAnswer
      };
    }
  };
  </script>
  
  <style scoped>
  .question-builder {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .builder-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }
  
  .builder-header h4 {
    margin: 0;
  }
  
  .builder-header p {
    margin: 5px 0 0;
    color: #747e79;
    font-size: 0.9rem;
  }
  
  .add-question-button {
    width: 100%;
    padding: 9px 14px;
    border: 1px solid #c8ded2;
    border-radius: 9px;
    background: #edf7f1;
    color: #178554;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .question-row {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px;
    border: 1px solid #e6eae8;
    border-radius: 10px;
    background: #fbfcfb;
  }
  
  .question-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
  }
  
  .remove-button {
    border: 0;
    background: transparent;
    color: #bd3d3d;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }
  
  .question-row label {
    display: flex;
    flex-direction: column;
    gap: 7px;
    color: #34413a;
    font-size: 0.9rem;
    font-weight: 600;
  }
  
  .question-row input,
  .question-row select {
    width: 100%;
    padding: 11px 12px;
    border: 1px solid #d7ded9;
    border-radius: 8px;
    box-sizing: border-box;
    background: #ffffff;
    color: #1f2a25;
    font: inherit;
  }

  .question-row select.quick-answer-placeholder,
  .quick-answer-placeholder-option {
    color: #87908b;
  }

  .quick-answer-option {
    color: #1f2a25;
  }

  .question-row .quick-answer-select:disabled {
    border-color: #e0e4e2;
    background: #f1f3f2;
    color: #a1aaa5;
    cursor: not-allowed;
    opacity: 1;
  }

  .reorder-control {
    display: flex;
    align-items: center;
    gap: 7px;
    min-height: 30px;
    color: #68736d;
    font-size: 0.82rem;
    font-weight: 600;
  }

  .reorder-divider {
    color: #c4cbc7;
  }

  .reorder-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid #c8ded2;
    border-radius: 7px;
    background: #edf7f1;
    color: #178554;
    cursor: pointer;
  }

  .reorder-button:hover:not(:disabled) {
    background: #dff1e7;
  }

  .reorder-button:disabled {
    border-color: #e0e4e2;
    background: #f1f3f2;
    color: #adb5b1;
    cursor: not-allowed;
  }

  .reorder-button .material-icons-outlined {
    font-size: 18px;
  }
  
  .question-options {
    display: grid;
    grid-template-columns:
      minmax(180px, 0.5fr)
      minmax(0, 1fr);
    gap: 14px;
  }
  
  .empty-state {
    padding: 18px;
    border: 1px dashed #d9dfdc;
    border-radius: 9px;
    color: #87908b;
    text-align: center;
  }
  
  @media (max-width: 700px) {
    .builder-header {
      align-items: flex-start;
      flex-direction: column;
    }
  
    .question-options {
      grid-template-columns: 1fr;
    }
  }
  </style>
